import { useCallback } from 'react'

import { useNavigate, useRouter } from '@tanstack/react-router'

import { queryClient } from '@/lib/react-query'

import { integrationQueries } from '@/features/integrations/queries'

import { userKeys } from '@/shared/auth/queries'

import { resolvePostSwitchPath } from '../post-switch-path'

import type { IntegrationConnector } from '@/features/integrations/types'
import type { Role, UserRequest } from '@/shared/auth/types'

/**
 * Fetch the target tenant's integrations so the smart-redirect can evaluate
 * integration gates. Returns `undefined` on failure — the caller then skips
 * the gate, matching the conservative behavior of `findMissingIntegrationGate`.
 */
const fetchTargetTenantIntegrations = async (
	tenantId: string,
): Promise<ReadonlyArray<IntegrationConnector> | undefined> => {
	try {
		return await queryClient.ensureQueryData(integrationQueries.list(tenantId))
	} catch {
		return undefined
	}
}

/**
 * Imperative tenant switcher for in-app UI (sidebar dropdown, settings page,
 * any future control that lets the user pick a tenant).
 *
 * Tenant switches are navigations. This hook:
 *   1. Fetches the target tenant's integrations so the smart-redirect can
 *      bail to `/home` on integration-gated routes (e.g. `/graylog/*` in a
 *      tenant without Graylog).
 *   2. Resolves a safe `targetPath` via {@link resolvePostSwitchPath} so the
 *      new URL doesn't carry a UUID from the old tenant (which would 401)
 *      and doesn't land on a route the target tenant can't render
 *      (insufficient role, missing integration).
 *   3. Navigates to `targetPath` with `?t=<tenantId>` in the URL.
 *
 * The actual switch flow — loader, cache clear, store update, prefetch — is
 * owned by the `_dashboard` route's `beforeLoad`, which detects the tenant
 * change via `loaderDeps` on `search.t` and orchestrates everything.
 *
 * Do NOT call `setCurrentTenant` directly anywhere. The store is updated
 * exclusively inside `seedDashboardSession`. For cross-tenant deep links
 * (multi-tenant list rows, share URLs), use `<Link search={{ t: tenantId }}>`
 * directly — going through this hook is only needed when the source path
 * may have a tenant-scoped UUID that won't exist in the target tenant.
 *
 * @returns switchTenant(tenantId) — async; resolves when the navigation
 *   is committed by TanStack Router. The `await` is for error propagation;
 *   route loader timing is independent.
 *
 * @see knowledge/reference/tenant-switching.md
 */
export const useSwitchTenant = () => {
	const navigate = useNavigate()
	const router = useRouter()

	const switchTenant = useCallback(
		async (tenantId: string | undefined) => {
			const meData = queryClient.getQueryData<UserRequest>(userKeys.me())
			const targetRole = tenantId
				? (meData?.tenant_roles[tenantId] as Role | undefined)
				: undefined

			const targetIntegrations = tenantId
				? await fetchTargetTenantIntegrations(tenantId)
				: undefined

			const targetPath = resolvePostSwitchPath(
				router.state.location.pathname,
				targetRole,
				targetIntegrations,
			)

			await navigate({ to: targetPath, search: { t: tenantId } })
		},
		[navigate, router],
	)

	return switchTenant
}
